import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { TenantRentalConfig } from "../../interfaces/TenantRentalConfig";
import * as tenantRentalConfigService from "../../services/tenantRentalConfigService";

interface TenantRentalConfigState {
  config: TenantRentalConfig | null;
  loading: boolean;
  error: string | null;
}

const initialState: TenantRentalConfigState = {
  config: null,
  loading: false,
  error: null,
};

// Async thunk to fetch tenant rental configuration
export const fetchRentalConfig = createAsyncThunk(
  "tenantRentalConfig/fetchRentalConfig",
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const response = await tenantRentalConfigService.getRentalConfig(tenantId);
      // Service returns null for timeouts or 404s - don't treat as error
      // Only reject for actual failures (network errors, 500, etc.)
      if (response === null) {
        // Return null to indicate no config found (not an error)
        return null;
      }
      return response;
    } catch (error: any) {
      // Only reject for actual errors, not timeouts or 404s (handled by service)
      // Check if it's a 401 - should be handled by axios interceptor
      if (error?.response?.status === 401) {
        // Let axios interceptor handle redirect
        return rejectWithValue("Unauthorized - session expired");
      }
      // For other errors, reject with message
      return rejectWithValue(error.message || "Failed to fetch rental configuration");
    }
  }
);

// Async thunk to create tenant rental configuration
export const createRentalConfig = createAsyncThunk(
  "tenantRentalConfig/createRentalConfig",
  async (data: Partial<TenantRentalConfig>) => {
    const response = await tenantRentalConfigService.createRentalConfig(data);
    return response;
  }
);

// Async thunk to update tenant rental configuration
export const updateRentalConfig = createAsyncThunk(
  "tenantRentalConfig/updateRentalConfig",
  async ({ id, data }: { id: string; data: Partial<TenantRentalConfig> }) => {
    const response = await tenantRentalConfigService.updateRentalConfig(
      id,
      data
    );
    return response;
  }
);

const tenantRentalConfigSlice = createSlice({
  name: "TenantRentalConfig",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    resetRentalConfig(state) {
      // Reset to initial state
      state.config = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch rental config
      .addCase(fetchRentalConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRentalConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null; // Clear any previous errors
        // If response is null, it means no data was found (timeout or 404) - not an error
        state.config = action.payload;
      })
      .addCase(fetchRentalConfig.rejected, (state, action) => {
        state.loading = false;
        // Only set error for actual failures, not timeouts or 404s
        const errorMessage = action.payload as string;
        // Don't set error for 401 - redirect handled by interceptor
        if (errorMessage && !errorMessage.includes("Unauthorized")) {
          state.error = errorMessage || "Failed to fetch rental configuration";
        } else {
          state.error = null;
        }
      })
      // Update rental config
      .addCase(updateRentalConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRentalConfig.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.config = action.payload;
        }
      })
      .addCase(updateRentalConfig.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to update rental configuration";
      })
      // Create rental config
      .addCase(createRentalConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRentalConfig.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.config = action.payload;
        }
      })
      .addCase(createRentalConfig.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to create rental configuration";
      });
  },
});

export const { clearError, resetRentalConfig } = tenantRentalConfigSlice.actions;
export default tenantRentalConfigSlice.reducer;

// Selectors
export const selectRentalConfig = (state: any) =>
  state.TenantRentalConfig.config;
export const selectRentalConfigLoading = (state: any) =>
  state.TenantRentalConfig.loading;
export const selectRentalConfigError = (state: any) =>
  state.TenantRentalConfig.error;